import { useEffect } from 'react'

import { reportError } from '@/spa/sentry'

export default function useEffectAsync(effect: () => void | Promise<void>, inputs: React.DependencyList) {
  useEffect(() => {
    // The returned promise USED to be dropped on the floor. A React error
    // boundary cannot see a rejected promise, so anything an async effect threw
    // — including CustomerLayout's bootstrap failure (NONRESIDENT-DB) — landed
    // on the browser's unhandled-rejection handler: `handled: no`, no component
    // or operation tag, and a minified stack pointing at React's commit phase
    // instead of the effect that failed.
    //
    // This does not swallow the failure. The effect's own catch still owns
    // recovery; this only guarantees the report is attributable when it has none.
    //
    // `effect` itself is intentionally not memoized: this hook forwards the
    // caller's own `inputs` array straight to `useEffect`, so React already
    // re-runs (and thus re-reads the latest `effect` closure) exactly when
    // `inputs` changes — a wrapping `useCallback` added nothing but an
    // incompatibility with react-compiler's "deps must be an array literal"
    // check, since `inputs` is a runtime value, not a literal.
    void Promise.resolve(effect()).catch((error: unknown) => {
      reportError(error, { tags: { component: 'use-effect-async' } })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, inputs)
}
